import { useLoading } from '@mxt/zio-react'
import { AppContext, Container, Input, Permission, Select, sharedStyle } from '@pulseops/common'
import { pipe } from 'fp-ts/lib/function'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import { ZIO } from '@mxt/zio'
import { CallOutDetailContext, OBCallOutPopupService, OBContainer, OBFieldDescription, OBFieldTitle, OBQcService, OBQualityControlForm, OBSectionCol, OBSectionContent, OBSectionRow, OBSharedStyles } from '@pulseops/outbound'
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, makeStyles } from '@material-ui/core'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import * as O from 'fp-ts/lib/Option'

type QCTopicList = {
  topicCode: string
  topicENDesc: string
  topicVNDesc: string
}

export const OBQCScreen = (props: { mailInfo: string, isREQC?: boolean }) => {
  const [loading, bindLoader] = useLoading(false)
  const { t, i18n } = useTranslation()
  const { processInstanceId, isDisabled, caseId, policyNumber, assignee, teamLeader, setIsSavedQCScreen,
    setIsNotInputedRequiredField, permissions, transactionType } = React.useContext(CallOutDetailContext)
  const { showToast, showGlobalLoading } = React.useContext(AppContext.AppContextInstance)
  const [qcHistoryList, setQcHistoryList] = React.useState<OBQcService.OBQcHistory | null>(null)
  const [qcTotalScorePass, setQcTotalScorePass] = React.useState<number>(0)
  const [disableSave, setDisableSave] = React.useState<boolean>(true)
  const [qcTotalScore, setQcTotalScore] = React.useState<number>(0)
  const [listTopic, setListTopic] = React.useState<QCTopicList[]>([])
  const [callReceiverList, setCallReceiverList] = React.useState<OBCallOutPopupService.CallReceiverInfo[]>([])
  const disableEditByPermission = !permissions.includes(Permission['EditOBQCTaskDetail'])
  const [isFinishFirstLoad, setFinishFirstLoad] = React.useState<boolean>(false)
  const [dataA12s6, setDataA12s6] = React.useState<OBQcService.A12S6Table[]>([])
  const [dataA12s2, setDataA12s2] = React.useState<OBQcService.A12S2Data[]>([])

  const useStyles = makeStyles({
    tableHeader: {
      backgroundColor: '#F4F4F4'
    },
    tableCell: {
      minWidth: 120,
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      color: '#70777E'
    },
    cellNoWrap: {
      whiteSpace: 'nowrap'
    }
  })

  const classes = useStyles()

  // const listCallProgramCS = [OBCallProgram.SQCB, OBCallProgram.RMD, OBCallProgram.CARE1, OBCallProgram.CARE2]
  // const listCallProgramCCE = [OBCallProgram.WCC, OBCallProgram.RMDBAU, OBCallProgram.M1, OBCallProgram.M2]

  // const isQcCCE = !!callProgram && listCallProgramCCE.includes(callProgram as OBCallProgram)
  // const isQcCS = !!callProgram && listCallProgramCS.includes(callProgram as OBCallProgram)

  const columnsQCList = [
    t('Outbound:OBQC:No'),
    t('Outbound:OBQC:Categories'),
    t('Outbound:OBQC:Criteria'),
    t('Outbound:OBQC:Descriptions'),
    t('Outbound:OBQC:FailureMechanism'),
    t('Outbound:OBQC:MaxScore'),
    t('Outbound:OBQC:Value')
  ]

  const formQC = useForm<OBQualityControlForm.OBQualityControlDataSave>({
    defaultValues: {
      qcDataList: [],
      comment: ''
    }
  })

  const { fields, append } = useFieldArray<OBQualityControlForm.OBQualityControlDataSave>({
    control: formQC.control,
    name: 'qcDataList'
  })

  React.useEffect(() => {
    showGlobalLoading(loading)
  }, [loading])

  React.useEffect(() => {
    pipe(
      ZIO.zipPar(
        OBQcService.getHistorySave(processInstanceId),
        OBCallOutPopupService.getCallingResultList(),
        OBQcService.getA12S6Table(),
        OBQcService.getDataScoreA12S2()
      ),
      ZIO.map(([res, callReceiverRes, tableA12S6, a12s2]) => {
        res && setQcHistoryList(res)
        tableA12S6 && setDataA12s6(tableA12S6)
        a12s2 && setDataA12s2(a12s2)
        setCallReceiverList(callReceiverRes)
        if (res.qcCommonInfo) {
          setQcTotalScorePass(res.qcCommonInfo.qcTotalPassScore ?? 0)
          setQcTotalScore(res.qcCommonInfo.qcTotalScore ?? 0)
          formQC.setValue('comment', res.qcCommonInfo.comment ?? '')
        }
        res.qcCriteriaList && setListTopic([...new Map(res.qcCriteriaList.map((item) => [item['a12s1Criteria'].topicCode,
        {
          topicCode: item.a12s1Criteria.topicCode ?? '',
          topicENDesc: item.a12s1Criteria.topicENDesc ?? '',
          topicVNDesc: item.a12s1Criteria.topicVNDesc ?? ''
        }
        ])).values()])

        res.qcCriteriaList.map((item) => {
          const dataAnwser = a12s2.find((x) => x.valueCode === item.answerValueCode)
          const itemQuestion = {
            criteriaCode: item.a12s1Criteria.criteriaCode ?? '',
            answerValueCode: dataAnwser ? {
              value: dataAnwser?.valueCode,
              label: i18n.language === 'vi' ? dataAnwser?.valueVN : dataAnwser?.valueEN
            } : null
          }
          append(itemQuestion)
        })

        setFinishFirstLoad(true)
      }),
      bindLoader,
      ZIO.unsafeRun({})
    )
  }, [processInstanceId])

  React.useEffect(() => {
    const qcDataList = formQC.watch('qcDataList')
    if (qcDataList && qcDataList.length > 0) {
      const isInValidData = qcDataList.some((item) => !item.answerValueCode || !item.answerValueCode.value)
      const isInValidComment = !formQC.watch('comment')
      setIsNotInputedRequiredField && setIsNotInputedRequiredField(isInValidData || isInValidComment)
    } else {
      setIsNotInputedRequiredField && setIsNotInputedRequiredField(false)
    }
  }, [formQC.watch('qcDataList')])

  const getListOptionValue = (typeA1: boolean) => {
    const optionValue = dataA12s2.map((item) => ({
      value: item.valueCode,
      label: i18n.language === 'vi' ? item.valueVN : item.valueEN
    }))
    return typeA1 ? optionValue : optionValue.filter((i) => i.value !== 'PWC')
  }

  const findIndexQuestion = (criteriaCode: string) => {
    const findIndex = fields.findIndex((item) => item.criteriaCode === criteriaCode)
    return findIndex
  }

  const checkDisableSave = () => {
    const findItem = formQC.watch('qcDataList').find((item) => item.answerValueCode)
    // findItem && setDisableSave(false)
    if (findItem) {
      setDisableSave(false)
      setIsSavedQCScreen && setIsSavedQCScreen(false)
    }
  }

  const getCallResultName = (callResultCode: string) => {
    return pipe(callReceiverList.find((x) => x.code === callResultCode), O.fromNullable, O.map((item) => i18n.language === 'en' ? item.nameEn : item.nameVi), O.getOrElse(() => ''))
  }

  const checkA7S6 = () => {
    const listFailureMechanism = qcHistoryList?.qcCriteriaList.filter((qc) => qc.a12s1Criteria.failureMechanism === 'Y')
    const checkFailureMechanism = formQC.watch('qcDataList').find((i) => i.answerValueCode?.value === 'F' && listFailureMechanism?.find((qc) => qc.a12s1Criteria.criteriaCode === i.criteriaCode))
    if(checkFailureMechanism) {
      return 'OC01'
    }
    else {
      const qcRatiing = Math.round((qcTotalScorePass / qcTotalScore) * 10000)/100
      const findItemCheckPass = dataA12s6.find((i) => i.transactionTypeWF === transactionType && i.comparisonOperator === '≥')
      const findItemCheckFail = dataA12s6.find((i) => i.transactionTypeWF === transactionType && i.comparisonOperator === '<')
  
      if(findItemCheckPass) {
        return (qcRatiing >= findItemCheckPass.qcRating) ? findItemCheckPass?.outcomeCode : (findItemCheckFail?.outcomeCode ?? '')
      }
    }
    return ''
  }

  const checkScoreValue = (value: string | undefined, oldValue: string | undefined, scoreMax: number) => {
    const scoreValue = dataA12s2.find((i => i.valueCode === value))?.valueScore
    const oldScoreValue = dataA12s2.find((i => i.valueCode === oldValue))?.valueScore

    if(scoreValue) {
      const newScore = scoreValue && !isNaN(Number(scoreValue)) ? Number(scoreValue) : 0
      const oldScore = oldScoreValue && !isNaN(Number(oldScoreValue)) ? Number(oldScoreValue) : 0
      switch (value) {
        case 'F': {
          if (!oldValue || oldValue === 'NA') setQcTotalScore(qcTotalScore + scoreMax)
          if ((oldValue === 'P' || oldValue === 'PWC')) setQcTotalScorePass(qcTotalScorePass - oldScore)
          return
        }
        case 'P': {
          if (!oldValue || oldValue === 'NA') setQcTotalScore(qcTotalScore + scoreMax)
          setQcTotalScorePass(qcTotalScorePass + newScore - oldScore)
          return
        }
        case 'NA': {
          if (oldValue) setQcTotalScore(qcTotalScore - scoreMax)
          if ((oldValue === 'P' || oldValue === 'PWC')) setQcTotalScorePass(qcTotalScorePass - oldScore)
          return
        }
        case 'PWC': {
          if (!oldValue || oldValue === 'NA') setQcTotalScore(qcTotalScore + scoreMax)
          setQcTotalScorePass(qcTotalScorePass + newScore - oldScore)
          return
        }
        default:
          return
      }
    }
    return
  }

  // const checkCCEScoreValue = (value: string | undefined, oldValue: string | undefined, maxScore: number) => {
  //   const numberValue = value && Number(value) > 0 ? Number(value) : 0
  //   const numberOldValue = oldValue && Number(oldValue) > 0 ? Number(oldValue) : 0
  //   if(value !== 'NA') {
  //     if (!oldValue || oldValue === 'NA') setQcTotalScore(qcTotalScore + maxScore)
  //   }
  //   else if(value === 'NA' && oldValue) {
  //     setQcTotalScore(qcTotalScore - maxScore)
  //   }
  //   setQcTotalScorePass(qcTotalScorePass - numberOldValue + numberValue)
  // }

  const saveHistory = async () => {
    const validate = await formQC.trigger()
    if (validate) {
      const qcList: OBQcService.QCList = formQC.watch('qcDataList').map((item) => {
        const findQcItem = (qcHistoryList && qcHistoryList.qcCriteriaList.find((qcItem) => qcItem.a12s1Criteria.criteriaCode === item.criteriaCode)?.a12s1Criteria) || {
          id: '',
          categoryENDesc: '',
          categoryVNDesc: '',
          criteriaENName: '',
          criteriaVNName: '',
          criteriaDescEN: '',
          criteriaDescVN: '',
          score: 0,
          topicCode: '',
          topicENDesc: '',
          topicVNDesc: '',
          answerTypeCode: '',
          failureMechanism: ''
        }
        return ({
          a12s1Criteria: {
            ...findQcItem,
            criteriaCode: item.criteriaCode ?? ''
          },
          answerValueCode: item.answerValueCode?.value ?? ''
        })
      })
      const qcRatiing = Math.round((qcTotalScorePass / qcTotalScore) * 10000)/100
      const mapDataSave: OBQcService.OBSaveDataQC = {
        businessKey: caseId,
        processInstanceId: processInstanceId,
        policyNumber: policyNumber,
        assignee: assignee,
        teamLeader: teamLeader,
        transactionType: transactionType ?? '',
        createdBy: props.mailInfo,
        createdDate: new Date().toISOString(),
        updatedBy: props.mailInfo,
        updatedDate: new Date().toISOString(),
        qcCommonInfo: {
          lastAssignee: qcHistoryList?.qcCommonInfo.lastAssignee,
          callResult: qcHistoryList?.qcCommonInfo.callResult,
          manualInternalFeedback: qcHistoryList?.qcCommonInfo.manualInternalFeedback,
          qcTotalPassScore: qcTotalScorePass,
          qcTotalScore: qcTotalScore,
          comment: formQC.watch('comment') ?? '',
          qcRating: qcRatiing,
          qcResult: checkA7S6(),
          updateContactInfo: qcHistoryList?.qcCommonInfo.updateContactInfo,
        },
        qcCriteriaList: qcList
      }
      pipe(
        OBQcService.saveHistoryQC(mapDataSave),
        ZIO.map((response) => {
          showGlobalLoading(false)
          setDisableSave(true)
          setIsSavedQCScreen && setIsSavedQCScreen(true)
          setIsNotInputedRequiredField && setIsNotInputedRequiredField(false)
          showToast(t('message:OB0040'), 'success')
          return response
        }),
        ZIO.catchAll((error) => {
          showGlobalLoading(false)
          showToast(t('message:OB0042'), 'error')
          return ZIO.fail(error)
        }),
        bindLoader,
        ZIO.unsafeRun({})
      )
    }
  }

  const QCInfo = () => {
    return (
      qcHistoryList && qcHistoryList?.qcCriteriaList.length > 0 &&
      <OBSectionContent style={{ marginBottom: 20 }}>
        <OBSectionRow>
          <OBSectionCol>
            <OBFieldTitle text={t('Outbound:OBQC:CallResult')}></OBFieldTitle>
            <OBFieldDescription text={qcHistoryList?.qcCommonInfo ? getCallResultName(qcHistoryList.qcCommonInfo.callResult || '')  : '-'}></OBFieldDescription>
          </OBSectionCol>
          <OBSectionCol>
            <OBFieldTitle text={t('Outbound:OBQC:ManualInternalFB')}></OBFieldTitle>
            <OBFieldDescription text={qcHistoryList?.qcCommonInfo?.manualInternalFeedback ? (qcHistoryList.qcCommonInfo.manualInternalFeedback === 'Y' ? 'Yes' : 'No') : '-'}></OBFieldDescription>
          </OBSectionCol>
          <OBSectionCol>
            <OBFieldTitle text={t('Outbound:OBQC:UpdateContactInfo')}></OBFieldTitle>
            <OBFieldDescription text={qcHistoryList?.qcCommonInfo?.updateContactInfo ? (qcHistoryList.qcCommonInfo.updateContactInfo === 'Y' ? 'Yes' : 'No') : '-'}></OBFieldDescription>
          </OBSectionCol>
        </OBSectionRow>
        <OBSectionRow style={{ paddingTop: 20}}>
          <OBSectionCol>
            <OBFieldTitle text={t('Outbound:OBQC:QCScore')}></OBFieldTitle>
            <OBFieldDescription text={qcTotalScorePass + '/' + qcTotalScore}></OBFieldDescription>
          </OBSectionCol>
          <OBSectionCol>
            <OBFieldTitle text={t('Outbound:OBQC:QCRatio')}></OBFieldTitle>
            <OBFieldDescription text={qcTotalScore > 0 ? `${Math.round((qcTotalScorePass / qcTotalScore) * 10000)/100} %` : '0 %'}></OBFieldDescription>
          </OBSectionCol>
          <OBSectionCol>
            <OBFieldTitle text={t('Outbound:OBQC:QCResult')}></OBFieldTitle>
            <OBFieldDescription text={checkA7S6() ? t(`Outbound:OBQC:${checkA7S6()}`) : '-'}></OBFieldDescription>
          </OBSectionCol>
        </OBSectionRow>
        <OBSectionRow style={{ paddingTop: 20}}>
          <OBSectionCol>
            <OBFieldTitle text={t('Outbound:OBQC:LastAsignee')}></OBFieldTitle>
            <OBFieldDescription text={qcHistoryList?.qcCommonInfo ? qcHistoryList.qcCommonInfo.lastAssignee ?? '' : '-'}></OBFieldDescription>
          </OBSectionCol>
        </OBSectionRow>
        <OBSectionRow style={{ paddingTop: 20}}>
          <View style={{ width: '100%', paddingHorizontal: 15 }}>
            <Controller
              control={formQC.control}
              name="comment"
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <Input
                  title={t('Outbound:OBQC:Comment')}
                  required
                  labelStyle={{ fontSize: 13, paddingTop: 5, paddingBottom: 10 }}
                  inputStyle={{ padding: 5 }}
                  disabled={isDisabled || disableEditByPermission || props.isREQC}
                  maxLength={1000}
                  value={value || ''}
                  multiline
                  numberOfLines={5}
                  onChange={(commentValue) => {
                    onChange(commentValue)
                    checkDisableSave()
                  }}
                  onBlur={onBlur}
                  errorMessage={error?.message}
                />
              )}
            />
          </View> 
        </OBSectionRow>
      </OBSectionContent>
    )
  }

  const TopicQC = (title: string, topicCode: string) => {
    const filterList = qcHistoryList ? qcHistoryList.qcCriteriaList.filter((item) => item.a12s1Criteria.topicCode === topicCode) : []
    return (
      <OBContainer containerTitle={title}>
        <View style={{ marginLeft: 20, marginRight: 20 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  {columnsQCList.map((colItem, index) => {
                    return (
                      columnsQCList.length === index + 1 ? // column Value
                      <TableCell className={classes.tableCell} key={`colItem_' - ${index}`}>{
                        <>
                          <Text style={{ fontWeight: 'bold', color: '#70777E' }}>{colItem}</Text>
                          <Text style={{ color: '#ed1b2c', marginLeft: 5, fontSize: 15, fontWeight: 'bold' }}>*</Text>
                        </>  
                      }</TableCell>
                      : <TableCell className={classes.tableCell} key={`colItem_' - ${index}`}>{colItem}</TableCell>
                    )
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {filterList.map((item, index) => (
                  <TableRow>
                    <TableCell style={{ width: '5%' }} className={classes.cellNoWrap}>{index + 1}</TableCell>
                    <TableCell style={{ width: '15%' }}>{i18n.language === 'vi' ? item.a12s1Criteria.categoryVNDesc ?? '-' : item.a12s1Criteria.categoryENDesc ?? '-'}</TableCell>
                    <TableCell style={{ width: '15%' }}>{i18n.language === 'vi' ? item.a12s1Criteria.criteriaVNName ?? '-' : item.a12s1Criteria.criteriaENName ?? '-'}</TableCell>
                    <TableCell style={{ width: '30%' }}>{i18n.language === 'vi' ? item.a12s1Criteria.criteriaDescVN ?? '-' : item.a12s1Criteria.criteriaDescEN ?? '-'}</TableCell>
                    <TableCell style={{ width: '10%' }}>{item.a12s1Criteria.failureMechanism ? (item.a12s1Criteria.failureMechanism ?? '-') : '-'}</TableCell>
                    <TableCell style={{ width: '8%' }} className={classes.cellNoWrap}>{item.a12s1Criteria.score ?? '-'}</TableCell>
                    <TableCell style={{ width: '12%' }} className={classes.cellNoWrap}>{
                      findIndexQuestion(item.a12s1Criteria.criteriaCode ?? '') !== -1 ?
                        <Controller
                          control={formQC.control}
                          name={`qcDataList.${findIndexQuestion(item.a12s1Criteria.criteriaCode ?? '') ?? index}.answerValueCode`}
                          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                            <Select
                              value={value}
                              onChange={(statusVal) => {
                                onChange(statusVal)
                                checkDisableSave()
                                checkScoreValue(statusVal?.value, value?.value, item.a12s1Criteria.score ?? 0)
                              }}
                              onBlur={onBlur}
                              disabled={isDisabled || disableEditByPermission || props.isREQC}
                              options={getListOptionValue(item.a12s1Criteria.answerTypeCode === 'A1')}
                              errorMessage=''
                            />
                          )}
                        /> : '-'
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </View>
      </OBContainer>
    )
  }

  const TopicQCList = () => {
    return isFinishFirstLoad && listTopic.map((item) =>
      <View>
        {TopicQC(i18n.language === 'vi' ? item.topicVNDesc : item.topicENDesc, item.topicCode)}
      </View>
    )
  }

  const ActionTab = () => {
    return (
      <OBSectionRow>
        {(isDisabled || disableEditByPermission || props.isREQC) ? (
          <></>
        ) : (
          qcHistoryList && qcHistoryList?.qcCriteriaList.length > 0 &&
          <View style={[qcStyles.buttonContainer, { width: '100%', justifyContent: 'flex-start' }]}>
            <TouchableOpacity disabled={disableSave} style={[sharedStyle.button, disableSave ? sharedStyle.btnDisabled : sharedStyle.btnRed]} onPress={() => saveHistory()}>
              <Text style={[disableSave ? { color: '#B0B0B0' } : OBSharedStyles.actionHightLightText, { textAlign: 'center' }]}>{t('common:Save')}</Text>
            </TouchableOpacity>
          </View>
        )
        }
      </OBSectionRow>
    )
  }

  return (
    <Container loading={false}>
      <View style={qcStyles.container}>
        {QCInfo()}
        {TopicQCList()}
        {ActionTab()}
      </View>
    </Container>
  )
}

const qcStyles = StyleSheet.create({
  container: {
    display: 'flex',
    // minHeight: '100%',
    backgroundColor: '#fff',
    paddingHorizontal: 24,
    paddingVertical: 16
  },
  header: {
    fontWeight: '700',
    textTransform: 'uppercase',
    color: '#58647A',
    fontSize: 16,
    marginBottom: 16
  },
  buttonContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 70
  }
})